import { Search, Bell, ChevronDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "@/routes";
import { useQuery } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { useHealthAlertNotifications } from "@/hooks/useHealthAlertNotifications";
import { supabase } from "@/integrations/supabase/client";

export function MemberHeader() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const displayName = user?.user_metadata?.display_name || user?.email?.split("@")[0] || "Member";
  const initials = displayName.split(" ").map((n: string) => n[0]).join("").toUpperCase().slice(0, 2);

  // Activate real-time health alert toasts
  useHealthAlertNotifications();

  // Count unacknowledged health alerts
  const { data: alertCount } = useQuery({
    queryKey: ["member-health-alerts-count", user?.id],
    queryFn: async () => {
      if (!user) return 0;
      const { count, error } = await supabase
        .from("health_alerts")
        .select("id", { count: "exact", head: true })
        .eq("member_id", user.id)
        .eq("is_acknowledged", false);
      if (error) return 0;
      return count || 0;
    },
    enabled: !!user,
  });

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-border bg-background/95 px-6 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      {/* Search */}
      <div className="relative w-96">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          type="search"
          placeholder="Search appointments, records..."
          className="pl-10 bg-muted/50 border-0 focus-visible:ring-1"
        />
      </div>

      {/* Right Section */}
      <div className="flex items-center gap-4">
        {/* Notifications */}
        <Button variant="ghost" size="icon" className="relative" onClick={() => navigate(ROUTES.MEMBER_NOTIFICATIONS)}>
          <Bell className="h-5 w-5" />
          {(alertCount ?? 0) > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-[10px] font-medium text-destructive-foreground">
              {alertCount! > 9 ? "9+" : alertCount}
            </span>
          )}
        </Button>

        {/* User Menu */}
        <div className="flex items-center gap-3 rounded-lg px-2 py-1.5 transition-colors hover:bg-muted/50 cursor-pointer">
          <Avatar className="h-8 w-8">
            <AvatarImage src="" />
            <AvatarFallback className="bg-accent text-accent-foreground text-sm">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="hidden md:block">
            <p className="text-sm font-medium text-foreground">{displayName}</p>
            <Badge variant="outline" className="text-xs">Member</Badge>
          </div>
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        </div>
      </div>
    </header>
  );
}
